/**
 * /auto-sync-subtitles (SEO_BRIEF_04 item 16): the tool row, and the pure half of the page.
 *
 * THE PAGE IS A TIMING TOOL WITH A SECOND INPUT. Its row runs `framerate` then `shift`, the
 * engine's own ops, with the two controls a person would set by hand on /subtitle-fps-converter
 * and /shift-subtitles. The video's audio, matched against the cue timings on the server
 * (`POST /api/autosync`, server/pipeline/autosync.py), FILLS THOSE CONTROLS IN. Everything after
 * that is the tool page every timing tool already is: the run, the before/after of the changed
 * cues, the download in the format that was dropped. The text never changes and never leaves the
 * browser; only the times move.
 *
 * The answer is `new = old * scale + offset`, and the order of the ops is what makes it so:
 * `framerate` in 'rescale' mode multiplies every time by from / to (the scale), and `shift` then
 * adds the offset. The server names its scale by the frame-rate pair it is, so the page applies
 * it with the engine's exact rationals (24000/1001, not 23.976) rather than a float.
 *
 * Exported to `server/app/tools.json` by build/tools_export.mjs, as the AUTOSYNC channel.
 */

import type { ToolRow } from '../transforms/clean/schema.ts';
import { ALL_TEXT_READ, ALL_TEXT_WRITE } from '../transforms/clean/schema.ts';
import type { OptionsSchema } from '../transforms/timing/schema.ts';

/** The row: CLEAN's shared shape, with TIMING's wider options schema (it has time and rate boxes). */
export interface AutosyncRow extends Omit<ToolRow, 'options_schema'> {
  readonly options_schema: OptionsSchema;
}

export const AUTOSYNC_SLUG = 'auto-sync-subtitles';

/** The option keys the match fills in. Namespaced by op, as `opsFromValues` reads them. */
export const SYNC_KEYS = {
  delta: 'shift.delta',
  rescale: 'framerate.enabled',
  from: 'framerate.from',
  to: 'framerate.to',
} as const;

const RATE_CHOICES = [
  { value: '24000/1001', label: '23.976' },
  { value: '24/1', label: '24' },
  { value: '25/1', label: '25' },
  { value: '30000/1001', label: '29.97' },
  { value: '30/1', label: '30' },
] as const;

const AUTO_SYNC: AutosyncRow = {
  slug: AUTOSYNC_SLUG,
  // The brief's H1 for the page, sentence case like every label on the site.
  label: 'Auto sync subtitles',
  op: 'shift',
  ops: ['framerate', 'shift'],
  read_formats: ALL_TEXT_READ,
  write_formats: ALL_TEXT_WRITE,
  options_schema: {
    version: 1,
    groups: [
      {
        key: 'autosync',
        label: 'Timing to apply',
        help: 'Filled in when the subtitles are matched to the speech. You can change it by hand.',
        open: true,
        fields: [
          {
            type: 'duration',
            key: SYNC_KEYS.delta,
            label: 'Move everything by',
            help: 'Milliseconds, or hh:mm:ss.mmm. Negative moves subtitles earlier.',
            default: 0,
            allowNegative: true,
          },
          {
            type: 'boolean',
            key: SYNC_KEYS.rescale,
            label: 'Correct a frame rate mismatch',
            help:
              'For subtitles that start in sync and drift further out: they were timed for ' +
              'another frame rate.',
            default: false,
            children: [
              {
                type: 'framerate',
                key: SYNC_KEYS.from,
                label: 'Subtitles were made for',
                default: null,
                choices: RATE_CHOICES,
              },
              {
                type: 'framerate',
                key: SYNC_KEYS.to,
                label: 'Your video is',
                default: null,
                choices: RATE_CHOICES,
              },
            ],
          },
        ],
      },
    ],
  },
};

/** The one row, for the exporter. */
export const AUTOSYNC_TOOLS: readonly AutosyncRow[] = [AUTO_SYNC];

/* ── the answer, onto the page ─────────────────────────────────────────────────────────── */

/** `POST /api/autosync`'s 200 (contracts/api.md §3.9), the fields the page reads. */
export interface SyncAnswer {
  readonly offset_s: number;
  readonly scale: number;
  readonly fps_from: string | null;
  readonly fps_to: string | null;
  readonly confidence: number;
}

/** The server's rate names, as the rationals the engine's framerate boxes hold. */
const RATES: Readonly<Record<string, { readonly num: number; readonly den: number }>> = {
  '23.976': { num: 24000, den: 1001 },
  '24': { num: 24, den: 1 },
  '25': { num: 25, den: 1 },
  '29.97': { num: 30000, den: 1001 },
  '30': { num: 30, den: 1 },
};

/**
 * The option values that apply `answer`: the offset in the shift box (milliseconds, rounded),
 * and the frame-rate pair in the rescale boxes, switched on only when the answer has one. A
 * pair the page cannot name is refused rather than approximated: a rescale by the wrong rate is
 * a file that drifts the other way.
 */
export function syncValues(answer: SyncAnswer): Readonly<Record<string, unknown>> {
  const from = answer.fps_from === null ? null : RATES[answer.fps_from];
  const to = answer.fps_to === null ? null : RATES[answer.fps_to];
  if ((from === undefined || to === undefined) && answer.scale !== 1) {
    throw new Error(`no frame rate named ${answer.fps_from} -> ${answer.fps_to}`);
  }
  const rescale = from != null && to != null && answer.scale !== 1;
  return {
    [SYNC_KEYS.delta]: Math.round(answer.offset_s * 1000),
    [SYNC_KEYS.rescale]: rescale,
    [SYNC_KEYS.from]: rescale ? { ...from } : null,
    [SYNC_KEYS.to]: rescale ? { ...to } : null,
  };
}

/** Ticks per second, `contracts/cue-model.ts`. */
const TICKS_PER_SECOND = 120_000;

/**
 * The cue timings the server is sent, from the worker's preview rows: `[start_s, end_s]` and
 * nothing else. The text stays here (the page's privacy line says so), and so do a row's id and
 * index. Rows that end before they start are left out; the engine's parse can carry them.
 */
export function cueTimings(
  rows: readonly { readonly startTicks: number; readonly endTicks: number }[],
): [number, number][] {
  const out: [number, number][] = [];
  for (const row of rows) {
    const start = row.startTicks / TICKS_PER_SECOND;
    const end = row.endTicks / TICKS_PER_SECOND;
    if (Number.isFinite(start) && Number.isFinite(end) && start >= 0 && end >= start) {
      out.push([Math.round(start * 1000) / 1000, Math.round(end * 1000) / 1000]);
    }
  }
  return out;
}

/** What the page says the match did, as values for its sentence. */
export interface SyncSummary {
  /** 'none' when the offset rounds to zero. */
  readonly direction: 'later' | 'earlier' | 'none';
  /** The offset's size in seconds, two decimals, unsigned. */
  readonly seconds: string;
  /** The rate pair when the match rescaled, else null. */
  readonly rate: { readonly from: string; readonly to: string } | null;
  /** 0-100. */
  readonly percent: number;
}

export function summarise(answer: SyncAnswer): SyncSummary {
  const ms = Math.round(answer.offset_s * 1000);
  return {
    direction: ms === 0 ? 'none' : ms > 0 ? 'later' : 'earlier',
    seconds: (Math.abs(ms) / 1000).toFixed(2),
    rate:
      answer.scale !== 1 && answer.fps_from !== null && answer.fps_to !== null
        ? { from: answer.fps_from, to: answer.fps_to }
        : null,
    percent: Math.round(Math.max(0, Math.min(1, answer.confidence)) * 100),
  };
}
